import { FileText, Hash, Phone } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import Input from "../../ui/Input";
import Button from "../../ui/Button";
import * as validate from "../../../lib/validators";
import { checkPublicMeetingJoinApi } from "../../../api/publicMeetingJoinApi";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { decryptObject } from "../../../utils/meetingEncryption";

export interface JoinMeetingCheckValues {
  meetingId: string;
  mobile: string;
  caseNumber: string;
  readOnly?: boolean;
}

interface JoinMeetingCheckProps {
  onNext?: (values: JoinMeetingCheckValues) => void;
}

const emptyValues: JoinMeetingCheckValues = {
  meetingId: "",
  mobile: "",
  caseNumber: "",
  readOnly: false,
};

type JoinMeetingCheckField = Exclude<keyof JoinMeetingCheckValues, "readOnly">;

const validateField = (field: JoinMeetingCheckField, value: string) => {
  switch (field) {
    case "meetingId":
      return validate.meetingId(value);
    case "mobile":
      return validate.mobile(value);
    case "caseNumber":
      return validate.caseNumber(value);
  }
};

const JoinMeetingCheck = ({ onNext }: JoinMeetingCheckProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mid = searchParams?.get("mid");

  const [form, setForm] = useState(emptyValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const update = (field: JoinMeetingCheckField, value: any) => {
    setForm((current) => ({ ...current, [field]: value }));
    // Re-validate live only after the field has been blurred once.
    if (touched[field]) {
      setErrors((current: any) => ({
        ...current,
        [field]: validateField(field, value),
      }));
    }
  };

  const blur = (field: JoinMeetingCheckField) => {
    setTouched((current) => ({ ...current, [field]: true }));
    setErrors((current: any) => ({
      ...current,
      [field]: validateField(field, form[field]),
    }));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    const nextErrors: Record<string, string> = {
      meetingId: validate.meetingId(form.meetingId),
      mobile: validate.mobile(form.mobile),
      caseNumber: validate.caseNumber(form.caseNumber),
    };

    setErrors(nextErrors);
    setTouched({ meetingId: true, mobile: true, caseNumber: true });
    if (!validate.isClean(nextErrors)) return;

    try {
      const res = await checkPublicMeetingJoinApi({
        meetingId: form.meetingId.trim(),
        mobile: form.mobile.trim(),
        caseNumber: form.caseNumber.trim().toUpperCase(),
      });
      if (!res?.data?.status) {
        toast.error(res?.data?.message || "Something went wrong");
      } else {
        toast.success(res?.data?.message || "Success");
        onNext?.({
          meetingId: form.meetingId.trim(),
          mobile: form.mobile.trim(),
          caseNumber: form.caseNumber.trim().toUpperCase(),
        });
      }
    } catch (error: any) {
      toast.error(error?.message || error?.response?.data?.message);
    }
  };

  useEffect(() => {
    if (mid) {
      const handleDecode = async () => {
        const res = await decryptObject(mid);
        setForm((prevState) => ({
          ...prevState,
          meetingId: res?.mid || "",
          mobile: res?.mobile || "",
          caseNumber: res?.caseNumber || "",
          readOnly: true,
        }));
      };
      handleDecode();
    }
  }, [mid]);

  return (
    <div>
      <form onSubmit={handleSubmit} noValidate>
        <Input
          label="Meeting ID"
          required
          placeholder="eg: 123546"
          leftIcon={<Hash size={18} />}
          inputMode="numeric"
          maxLength={12}
          value={form.meetingId}
          error={errors.meetingId}
          onChange={(event) =>
            update("meetingId", event.target.value.replace(/\D/g, ""))
          }
          onBlur={() => blur("meetingId")}
          readOnly={form.readOnly}
        />
        <Input
          label="Mobile Number"
          required
          placeholder="10-digit mobile"
          leftIcon={<Phone size={18} />}
          type="tel"
          inputMode="numeric"
          maxLength={10}
          value={form.mobile}
          error={errors.mobile}
          onChange={(event) =>
            update("mobile", event.target.value.replace(/\D/g, ""))
          }
          onBlur={() => blur("mobile")}
        />
        <Input
          label="Case Number"
          required
          placeholder="e.g. 2024/CR/00741"
          leftIcon={<FileText size={18} />}
          maxLength={16}
          value={form.caseNumber}
          error={errors.caseNumber}
          onChange={(event) => update("caseNumber", event.target.value)}
          onBlur={() => blur("caseNumber")}
        />
        <Button type="submit" fullWidth className="mt-5">
          Continue
        </Button>
        <button
          type="button"
          onClick={() => navigate("/")}
          className="mt-4 block w-full text-sm font-medium text-blue-200 transition hover:text-white"
        >
          Back to Sign In
        </button>
      </form>
    </div>
  );
};

export default JoinMeetingCheck;
